import { TextInput } from '@curology/ui-components-web/react';
import { Button } from '@curology/ui-components-web/react/Button';
import { CrossIcon } from '@curology/ui-components-web/react/Icon/icons/CrossIcon';
import { Modal } from '@curology/ui-components-web/react/Modal';
import { ModalContent } from '@curology/ui-components-web/react/Modal/ModalContent';
import { ModalFooter } from '@curology/ui-components-web/react/Modal/ModalFooter';
import { ModalHeader } from '@curology/ui-components-web/react/Modal/ModalHeader';
import { SelectInput } from '@curology/ui-components-web/react/SelectInput';
import { SelectOption } from '@curology/ui-components-web/react/SelectInput/SelectOption';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { useAtom } from 'jotai/index';
import React, { useEffect, useState } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import {
  getSuggestions,
  splitAddressStringIntoParts,
} from '@/components/checkout/js/smarty';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { ALL_STATES } from '@/consts/states';
import { ZIP_CODE_REGEX } from '@/consts/validation';
import { ResubscriptionService } from '@/services/resubscriptionService/client';
import { type Patient } from '@/services/resubscriptionService/types';
import type { ShippingAddressFields } from '@/types/cartCheckout/types';
import type { SmartySuggestion } from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

const ShippingAddressModal = ({
  setShowModal,
  showModal,
  user,
  setPatient,
  onSuccess,
}: {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  user: Patient;
  setPatient: React.Dispatch<React.SetStateAction<Patient>>;
  onSuccess: () => void;
}) => {
  const { brand } = useFlowContext();
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<SmartySuggestion[]>([]);
  const [isZipCodeValid, setIsZipCodeValid] = useState<boolean>(true);
  const zipCodeState = isZipCodeValid ? '' : 'critical';
  const stateOptions: { [key: number | string]: string } = ALL_STATES;

  const [shippingAddressFields, setShippingAddressFields] =
    useState<ShippingAddressFields>({
      apartmentSuite: user.address2 || '',
      city: user.city || '',
      firstName: user.firstname,
      lastName: user.lastname,
      state: user.state || '',
      streetAddress: user.address1 || '',
      zipCode: user.zipcode || '',
    });

  const areRequiredFieldsEntered = !!(
    shippingAddressFields.firstName &&
    shippingAddressFields.lastName &&
    shippingAddressFields.streetAddress &&
    shippingAddressFields.city &&
    shippingAddressFields.state &&
    shippingAddressFields.zipCode
  );

  const onShippingAddressUpdateFailure = () => {
    setFlashMessages([
      ...flashMessages,
      {
        children: 'An error occurred while updating the shipping address.',
        state: 'critical',
      },
    ]);
    setIsSaving(false);
    trackEvent(AnalyticsEvent.RESUBSCRIBE_INFORMATION_UPDATE_FAILED, {
      moduleName: 'shipping',
    });
  };

  const onSubmit = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    const addressUpdated = await ResubscriptionService.updateShippingAddress(
      brand,
      setPatient,
      shippingAddressFields,
      () => onShippingAddressUpdateFailure(),
    );

    if (addressUpdated) {
      onSuccess();
      setIsSaving(false);
      setShowModal(false);
    }
  };

  useEffect(() => {
    const setUserData = async () => {
      setShippingAddressFields({
        apartmentSuite: user.address2 || '',
        city: user.city || '',
        firstName: user.firstname,
        lastName: user.lastname,
        state: user.state || '',
        streetAddress: user.address1 || '',
        zipCode: user.zipcode || '',
      });
    };
    setUserData();
  }, [user]);

  return (
    <Modal
      onClose={() => setShowModal(false)}
      open={showModal}
      title={<ModalHeader>Shipping Address</ModalHeader>}
      type="light"
      contentStyle="card"
      width={576}
    >
      <ModalContent className="cur-bg-[#ffffff] cur-p-4">
        <TextInput
          required
          label="First name"
          className="data-hj-suppress"
          onChange={(e) => {
            setShippingAddressFields({
              ...shippingAddressFields,
              firstName: e.target.value,
            });
          }}
          value={shippingAddressFields.firstName}
        />
        <TextInput
          required
          label="Last name"
          className="data-hj-suppress"
          onChange={(e) => {
            setShippingAddressFields({
              ...shippingAddressFields,
              lastName: e.target.value,
            });
          }}
          value={shippingAddressFields.lastName}
        />
        <TextInput
          required
          label="Address"
          onChange={(e) => {
            setShippingAddressFields({
              ...shippingAddressFields,
              streetAddress: e.target.value,
            });
            getSuggestions(brand, e.target.value, setSuggestions);
          }}
          value={shippingAddressFields.streetAddress}
        />
        {suggestions.length > 0 && (
          <div className="cur-relative data-hj-suppress">
            <div className="cur-absolute cur-bg-surface-neutral cur-border cur-border-disabled cur-p-3 cur-top-[-1px] cur-w-full cur-z-10">
              <div className="cur-flex cur-items-center cur-justify-between">
                <Typography className="cur-text-disabled">
                  Suggestions
                </Typography>
                <Button
                  buttonType="text"
                  data-sentry-id="Close address pre-fill suggestions"
                  iconStart={CrossIcon}
                  onClick={() => setSuggestions([])}
                  size="sm"
                  state="dark"
                />
              </div>
              <ul className="cur-mb-0 cur-mt-3">
                {suggestions.map((suggestion, index) => {
                  const {
                    city,
                    state,
                    street_line: streetAddress,
                    zipcode: zipCode,
                  } = suggestion;

                  const searchString = shippingAddressFields.streetAddress;
                  const addressString = `${streetAddress}, ${city}, ${state} ${zipCode}`;

                  const addressStringParts = splitAddressStringIntoParts(
                    searchString,
                    addressString,
                  );

                  return (
                    <li key={index}>
                      <button
                        className="cur-overflow-hidden cur-py-2 cur-text-ellipsis cur-text-left cur-w-full cur-whitespace-nowrap"
                        data-sentry-id="Select address pre-fill suggestion"
                        onClick={() => {
                          setShippingAddressFields({
                            ...shippingAddressFields,
                            city,
                            state,
                            streetAddress,
                            zipCode,
                          });

                          setSuggestions([]);

                          trackEvent(
                            AnalyticsEvent.ADDRESS_SUGGESTION_SELECTED,
                          );
                        }}
                        type="button"
                      >
                        {addressStringParts.map((addressStringPart, index) => {
                          return (
                            <Typography
                              className="cur-inline"
                              key={index}
                              weight={
                                addressStringPart.style === 'bold'
                                  ? 'bold'
                                  : 'normal'
                              }
                            >
                              {addressStringPart.substring}
                            </Typography>
                          );
                        })}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
        <TextInput
          label="Apartment, suite, etc."
          onChange={(e) => {
            setShippingAddressFields({
              ...shippingAddressFields,
              apartmentSuite: e.target.value,
            });
          }}
          value={shippingAddressFields.apartmentSuite}
        />
        <TextInput
          label="City"
          onChange={(e) => {
            setShippingAddressFields({
              ...shippingAddressFields,
              city: e.target.value,
            });
          }}
          value={shippingAddressFields.city}
        />
        <div className="cur-flex cur-w-full cur-gap-2 data-hj-suppress">
          <SelectInput
            label="State"
            onChange={(e) => {
              setShippingAddressFields({
                ...shippingAddressFields,
                state: e.target.value,
              });
            }}
            value={shippingAddressFields.state}
            className="cur-w-[50%]"
          >
            {Object.keys(stateOptions).map((key) => {
              return (
                <SelectOption key={key} value={key}>
                  {stateOptions[key]}
                </SelectOption>
              );
            })}
          </SelectInput>
          <TextInput
            label="Zip code"
            onChange={(e) => {
              setShippingAddressFields({
                ...shippingAddressFields,
                zipCode: e.target.value,
              });
            }}
            onBlur={(e) => {
              setIsZipCodeValid(
                e.target.value.trim() === '' ||
                  ZIP_CODE_REGEX.test(e.target.value),
              );
            }}
            value={shippingAddressFields.zipCode}
            state={zipCodeState}
            helperText={isZipCodeValid ? '' : 'Zip code is invalid.'}
            className="cur-w-[50%]"
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <Button
          className="cur-w-full"
          data-sentry-id="Close modal"
          onClick={() => onSubmit()}
          size="lg"
          disabled={!areRequiredFieldsEntered}
          loading={isSaving}
        >
          Save
        </Button>
        <Button
          className="cur-w-full"
          data-sentry-id="Close modal"
          onClick={() => setShowModal(false)}
          size="lg"
          buttonType="secondary"
        >
          Back
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ShippingAddressModal;
